import { useEffect, useState, useMemo } from "react";
import apiClient from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Eye, Search, MessageSquare, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { v: "new",                  l: "New",                  tone: "bg-blue-100 text-blue-800" },
  { v: "reviewed",             l: "Reviewed",             tone: "bg-purple-100 text-purple-800" },
  { v: "contacted",            l: "Contacted",            tone: "bg-amber-100 text-amber-800" },
  { v: "follow_up",            l: "Follow Up Required",   tone: "bg-orange-100 text-orange-800" },
  { v: "consultation_booked",  l: "Consultation Booked",  tone: "bg-emerald-100 text-emerald-800" },
  { v: "closed",               l: "Closed",               tone: "bg-slate-200 text-slate-700" },
];

const STATUS_MAP = Object.fromEntries(STATUS_OPTS.map((s) => [s.v, s]));

export default function AdminChatEnquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => {
    setLoading(true);
    apiClient.get("/admin/chat/enquiries").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (!ql) return true;
      return (i.full_name + " " + i.email + " " + i.enquiry_type + " " + (i.message || "")).toLowerCase().includes(ql);
    });
  }, [items, q, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const s of STATUS_OPTS) c[s.v] = items.filter((i) => i.status === s.v).length;
    return c;
  }, [items]);

  const updateStatus = async (id, status) => {
    try {
      await apiClient.patch(`/admin/chat/enquiries/${id}`, { status });
      setItems((arr) => arr.map((i) => i.id === id ? { ...i, status } : i));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      await apiClient.patch(`/admin/chat/enquiries/${selected.id}`, { notes });
      setItems((arr) => arr.map((i) => i.id === selected.id ? { ...i, notes } : i));
      toast.success("Notes saved");
    } catch { toast.error("Save failed"); }
  };

  const openDetail = (item) => { setSelected(item); setNotes(item.notes || ""); };

  return (
    <div className="p-6 md:p-8" data-testid="admin-chat-enquiries-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[#29324A]">Chat Enquiries</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} total · {counts.new || 0} new</p>
        </div>
        <button onClick={load} className="text-sm text-[#29324A] hover:text-[#C43A89] inline-flex items-center gap-1.5" data-testid="chat-enq-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input data-testid="chat-enq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, enquiry…" className="pl-9 w-72" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} label="All" count={counts.all} />
          {STATUS_OPTS.map((s) => (
            <FilterChip key={s.v} active={statusFilter === s.v} onClick={() => setStatusFilter(s.v)} label={s.l} count={counts[s.v]} testId={`filter-${s.v}`} />
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <MessageSquare className="h-8 w-8 mx-auto mb-3 text-slate-300" />
            No chat enquiries match.
          </div>
        ) : (
          <Table data-testid="chat-enquiries-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Enquiry</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => {
                const tone = STATUS_MAP[item.status]?.tone || "bg-slate-100 text-slate-700";
                return (
                  <TableRow key={item.id} data-testid={`chat-enq-row-${item.id}`}>
                    <TableCell className="text-sm font-medium">{item.full_name}</TableCell>
                    <TableCell className="text-sm">{item.email}</TableCell>
                    <TableCell className="text-sm">{item.phone || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {item.location === "onshore" ? `Onshore · ${item.current_visa || "—"}` : "Offshore"}
                    </TableCell>
                    <TableCell className="text-sm">{item.enquiry_type}</TableCell>
                    <TableCell className="text-xs">{item.current_stage}</TableCell>
                    <TableCell>
                      <span className={`text-[11px] font-bold tracking-wider uppercase px-2 py-1 ${tone}`}>{STATUS_MAP[item.status]?.l || item.status}</span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(item.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</TableCell>
                    <TableCell>
                      <button onClick={() => openDetail(item)} className="text-[#29324A] hover:text-[#C43A89] inline-flex items-center gap-1 text-sm" data-testid={`chat-enq-view-${item.id}`}>
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Chat Enquiry — {selected?.full_name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Detail k="Email" v={selected.email} />
                <Detail k="Phone" v={selected.phone || "—"} />
                <Detail k="Preferred contact" v={selected.contact_pref} />
                <Detail k="Location" v={selected.location === "onshore" ? `Onshore · ${selected.current_visa || "—"}` : "Offshore"} />
                <Detail k="Enquiry type" v={selected.enquiry_type} />
                <Detail k="Current stage" v={selected.current_stage} />
                <Detail k="Submitted" v={new Date(selected.created_at).toLocaleString("en-AU", { dateStyle: "long", timeStyle: "short" })} />
                <Detail k="IP" v={selected.ip || "—"} />
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold tracking-wider text-slate-500 uppercase">Status</span>
                <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                  <SelectTrigger className="w-64" data-testid="chat-enq-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Original message */}
              <div>
                <div className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-1.5">Initial message</div>
                <div className="bg-slate-50 border-l-4 border-[#29324A] p-4 text-[13.5px] leading-relaxed text-slate-700 whitespace-pre-line">{selected.message}</div>
              </div>

              {/* Conversation */}
              <div>
                <div className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-2">Conversation ({(selected.messages || []).length} messages)</div>
                <div className="space-y-2 max-h-80 overflow-y-auto pr-2 bg-[#FAF7FC] border border-slate-200 p-3">
                  {(selected.messages || []).map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-1.5">Internal notes (visible to admins only)</div>
                <textarea data-testid="chat-enq-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="w-full text-sm border border-slate-200 focus:border-[#29324A] outline-none p-3" placeholder="Add follow-up notes, call outcomes, next steps…" />
                <div className="flex justify-end mt-2">
                  <button onClick={saveNotes} className="bg-[#29324A] hover:bg-[#1f2638] text-white text-xs font-bold tracking-wider uppercase px-4 py-2" data-testid="chat-enq-save-notes">Save notes</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`text-xs font-medium px-3 py-1.5 border transition-colors ${active ? "bg-[#29324A] border-[#29324A] text-white" : "bg-white border-slate-200 text-slate-700 hover:border-[#29324A]"}`}>
      {label} <span className={`ml-1 text-[10px] ${active ? "text-[#C43A89]" : "text-slate-400"}`}>{count ?? 0}</span>
    </button>
  );
}

function Detail({ k, v }) {
  return (
    <div className="py-1.5">
      <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">{k}</div>
      <div className="text-[13.5px] text-slate-800 break-words">{v}</div>
    </div>
  );
}
